import React from 'react';
import { GameState, CardColor } from '../types';
import { TowerCrystal } from '../constants';
import * as gameLogic from '../services/gameLogic';
import { Language, t } from '../data/translations';
import { useShellLayout } from '../hooks/useShellLayout';

const colorHexMap: Record<CardColor, string> = {
    [CardColor.Red]: '#ef4444', [CardColor.Pink]: '#ec4899', [CardColor.Green]: '#22c55e',
    [CardColor.Black]: '#64748b', [CardColor.Purple]: '#a855f7', [CardColor.Yellow]: '#eab308',
};

const TowerChangeModal: React.FC<{
    gameState: GameState;
    onChoose: (color: CardColor) => void;
    onCancel: () => void;
    language: Language;
}> = ({ gameState, onChoose, onCancel, language }) => {
    if (!gameState.towerChangeMenu) return null;

    const availableColors = gameLogic.getAvailableColorsForTowerChange(gameState);
    const allColors = Object.values(CardColor);
    const { isMobileShell } = useShellLayout();

    return (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-40">
            <div className={`flex items-center justify-center ${isMobileShell ? 'flex-row gap-8 scale-[0.85]' : 'flex-col gap-0'}`}>
                <div className={`flex flex-col items-center justify-center ${isMobileShell ? 'w-1/2 text-left' : 'w-full text-center'}`}>
                    <h2 className="text-4xl font-bold mb-4 text-slate-200 text-shadow-lg leading-tight">
                        {t('ui.transformTower', language)}
                    </h2>
                    <p className={`text-slate-400 max-w-md ${isMobileShell ? 'mb-4' : 'mb-8'}`}>{t('ui.towerChangeDesc', language)}</p>
                    {isMobileShell && (
                        <button
                            onClick={onCancel}
                            className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-300 w-full max-w-[200px]"
                        >
                            {t('ui.cancel', language)}
                        </button>
                    )}
                </div>
                
                <div className={`grid grid-cols-3 gap-8 ${isMobileShell ? 'w-1/2' : 'mb-8'}`}>
                    {allColors.map(color => {
                        const canAfford = availableColors.includes(color);
                        const isCurrent = color === gameState.towerChangeMenu?.currentColor;

                        if (isCurrent) return null;

                        return (
                            <div key={color}
                                className={`flex flex-col items-center justify-center gap-3 p-4 transition-all duration-300 ${canAfford ? 'cursor-pointer transform hover:scale-110' : 'opacity-40'}`}
                                onClick={canAfford ? () => onChoose(color) : undefined}
                            >
                                <TowerCrystal color={color} className="w-20 h-20" />
                                <span className="font-bold text-lg" style={{ color: colorHexMap[color] }}>{t(`term.${color.toLowerCase()}` as any, language)}</span>
                                {!canAfford && (
                                    <div className="text-xs text-slate-400 whitespace-nowrap">{t('ui.noColorCard', language, { color: t(`term.${color.toLowerCase()}` as any, language) })}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {!isMobileShell && (
                <button
                    onClick={onCancel}
                    className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-300 mt-4"
                >
                    {t('ui.cancel', language)}
                </button>
            )}
        </div>
    );
};

export default TowerChangeModal;
